import axios from 'axios';
import { BUILD_INFO } from '../buildInfo';
import { isChunkLoadError } from './lazyWithRetry';

// item 1525-web (Chad) — CLIENT ERROR REPORTER. A browser-side crash used to live only in the
// user's console (Tom: "it just stopped working"); now every uncaught error / unhandled promise
// rejection POSTs a compact report to api/ClientError (authenticated — the app's axios carries
// the bearer via App.tsx's interceptor, which is why this is installed INSIDE the auth gate).
// Production only. Capped client-side at 5 reports/min so a render loop can't flood the API.
// Every failure to report is swallowed — the reporter must never become its own error source.
const apiUrl = () => process.env.REACT_APP_BASIX_API || '';
const MAX_PER_MINUTE = 5;
const STACK_LIMIT = 2000;

let installed = false;
const sentAt: number[] = [];

const underCap = (): boolean => {
  const now = Date.now();
  while (sentAt.length && now - sentAt[0] > 60_000) sentAt.shift();
  if (sentAt.length >= MAX_PER_MINUTE) return false;
  sentAt.push(now);
  return true;
};

// noise we deliberately don't report: stale-chunk loads (item 1515 reloads once for those) and
// the benign "ResizeObserver loop limit exceeded / completed with undelivered notifications"
const isNoise = (message: string, err: unknown): boolean => isChunkLoadError(err)
  || /Loading (CSS )?chunk/i.test(message)
  || /ResizeObserver loop/i.test(message);

const describe = (err: unknown, fallbackMessage?: string): { message: string; stack: string } => {
  if (err instanceof Error) {
    return { message: err.message || fallbackMessage || 'Error', stack: String(err.stack ?? '').slice(0, STACK_LIMIT) };
  }
  if (typeof err === 'string') return { message: err, stack: '' };
  if (err && typeof err === 'object') {
    const o = err as { message?: unknown; stack?: unknown };
    const message = typeof o.message === 'string' ? o.message : fallbackMessage;
    let stack = typeof o.stack === 'string' ? o.stack : '';
    if (!message && !stack) {
      try { stack = JSON.stringify(err).slice(0, STACK_LIMIT); } catch { stack = ''; }
    }
    return { message: message || 'Non-Error rejection', stack: stack.slice(0, STACK_LIMIT) };
  }
  return { message: fallbackMessage || String(err ?? 'Unknown error'), stack: '' };
};

export const reportClientError = (err: unknown, source?: string, fallbackMessage?: string): void => {
  try {
    if (process.env.NODE_ENV !== 'production') return;
    const { message, stack } = describe(err, fallbackMessage);
    if (isNoise(message, err)) return;
    if (!underCap()) return;
    const payload = {
      message: source ? `[${source}] ${message}` : message,
      stack,
      url: window.location.href,
      build: BUILD_INFO.commit,
      userAgent: navigator.userAgent,
    };
    axios.post(`${apiUrl()}ClientError`, payload).catch(() => { /* swallow — may 404 until the endpoint ships */ });
  } catch {
    /* never throw from the reporter */
  }
};

export const installClientErrorReporter = (): void => {
  if (installed || process.env.NODE_ENV !== 'production') return;
  installed = true;
  window.addEventListener('error', (ev: ErrorEvent) => {
    reportClientError(ev.error ?? ev.message, 'window.onerror', ev.message);
  });
  window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
    reportClientError(ev.reason, 'unhandledrejection');
  });
};

export default installClientErrorReporter;
